import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Flex, Heading, Spinner, Text } from "@telia/teddy";
import { API_URL_AGENT_FEEDBACK, API_URL_MY_FEEDBACK } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import UserFeedbackCard from "./UserFeedbackCard";
import { type FeedbackEntry } from "../SuperAdmin/feedbackTypes";
import SendFeedbackPanel from "../../components/molecules/SendFeedbackPanel/SendFeedbackPanel";
import "./Feedbacks.less";

const Feedbacks: React.FC = () => {
    const { salesAgentId } = useParams<{ salesAgentId?: string }>();
    const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);

    useEffect(() => {
        const fetchFeedback = async () => {
            setLoading(true);
            setError(null);
            try {
                const url = salesAgentId ? `${API_URL_AGENT_FEEDBACK}/${salesAgentId}` : API_URL_MY_FEEDBACK;
                const res = await retailerFetch(url);
                if (res.status === 403) {
                    setError("Du har ikke tilgang til å se denne brukerens tilbakemeldinger.");
                    return;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: FeedbackEntry[] = await res.json();
                setFeedback(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Kunne ikke hente tilbakemeldinger");
            } finally {
                setLoading(false);
            }
        };
        fetchFeedback();
    }, [salesAgentId]);

    const agentName = salesAgentId ? feedback[0]?.agentName : undefined;
    const title = salesAgentId ? `Tilbakemeldingene til ${agentName ?? salesAgentId}` : "Dine tilbakemeldinger";

    return (
        <Box className="feedbacks-page">
            <Heading mb="100" as="h1" variant="title-300" className="feedbacks-page__header">
                {title}
            </Heading>

            {loading && (
                <Box className="feedbacks-page__loading">
                    <Spinner size="md" />
                </Box>
            )}

            {error && (
                <Box className="feedbacks-page__error">
                    <Text>{error}</Text>
                </Box>
            )}

            {!loading && !error && feedback.length === 0 && (
                <Box className="feedbacks-page__empty">
                    <Text className="feedbacks-page__empty-text">Du har ikke gitt noen tilbakemeldinger på Nexus</Text>
                    <Button variant="primary" onClick={() => setFeedbackPanelOpen(true)}>
                        Gi tilbakemelding
                    </Button>
                </Box>
            )}

            {!loading && !error && feedback.length > 0 && (
                <>
                    <Flex direction="column" gap="200">
                        {feedback.map((entry) => (
                            <UserFeedbackCard key={entry.id} entry={entry} shouldShowName={!!salesAgentId} />
                        ))}
                    </Flex>
                </>
            )}

            <SendFeedbackPanel open={feedbackPanelOpen} onOpenChange={setFeedbackPanelOpen} />
        </Box>
    );
};

export default Feedbacks;
